import { storage } from '@doxyme/ui/utils';
import { useHotpot } from '@doxyme/hotpot/react';
import {
  getApolloSessionDetails,
  getIsAudioCall,
} from '../../../features/apolloSession/apolloSession.selectors';
import { setIsAudioCall } from '../../../features/apolloSession/apolloSession.slice';
import { setDialedPhoneNumber } from '../../../features/uiState/uiState.slice';
import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import * as realtime from '../../../features/realtime/realtime.slice';
import {
  getIdentityId,
  getIsProvider,
} from '../../../features/realtime/realtime.selectors';
import { useSession } from '../../useSession';
import { getLogger, updateLoggerContext } from '../../../libs/logger';
import { RealtimeMemberStatus } from '../../../features/realtime/realtime.types';
import { promptPostCallSurvey } from '../../../features/postCallSurvey';
import { useExtensionsTranscription } from '../../useExtensions/useExtensionsTranscription';
import { useService } from '../../useService';
import { useGetTime } from '../../useGetTime';
import { useIsPhoneCall } from '../../useIsPhoneCall';

const logger = getLogger('useCallStateLeaving');

type UseEndSessionReturnType = {
  endSession: () => Promise<void>;
  endSessionBecauseProviderLeft: () => Promise<void>;
  endPatientSessionForTransfer: () => Promise<void>;
};

export const useEndSession = (): UseEndSessionReturnType => {
  const identityId = useAppSelector(getIdentityId);
  const apolloSession = useAppSelector(getApolloSessionDetails);
  const isProvider = useAppSelector(getIsProvider);
  const dispatch = useAppDispatch();
  const { end: dispatchSessionEnd } = useSession();
  const isAudioCall = useAppSelector(getIsAudioCall);
  const isPhoneCall = useIsPhoneCall();
  const callHistory = useService('callHistory');
  const { stop: stopTranscription } = useExtensionsTranscription();
  const hotpot = useHotpot();
  const getTime = useGetTime();

  const endSession = async (): Promise<void> => {
    logger.info('[endSession] Session has ended', {
      identityId,
      isAudioCall,
    });

    // Logic borrowed from OG
    //  https://gitlab.com/doxyme/app/doxyme-frontend/-/blob/qa/src/app/video-chat/video-chat.realtime-component.ts?ref_type=heads#L798
    const SURVEY_RATE = 10; // 1 in x
    const shouldSurvey = Math.floor(Math.random() * SURVEY_RATE) === 0;
    const shouldSurveyTestOverride = storage.getItem(
      'testing-always-show-survey',
      true,
    );

    if (shouldSurvey || shouldSurveyTestOverride) {
      dispatch(promptPostCallSurvey);
    }

    if (isAudioCall) {
      dispatch(setIsAudioCall(false));
    }

    if (isPhoneCall) {
      dispatch(setDialedPhoneNumber(null));
    }

    try {
      void callHistory.recordCallEnd({
        dbSessionId: apolloSession.dbSessionId,
        roomId: !isProvider ? apolloSession.roomId : undefined,
      });
      await dispatchSessionEnd();

      if (isProvider) {
        stopTranscription?.();
        await dispatch(
          realtime.updateRealtimeState({
            status: RealtimeMemberStatus.Online,
            inCallWithProviderId: undefined,
            inCallWithProviderName: undefined,
          }),
        );
        // Reset roomId for provider after call ends
        updateLoggerContext({ roomId: undefined });
        return;
      }
      // Clear Hotpot so a later check-in (e.g. after back back button clicked) is a fresh session; keeps Hotpot in sync with core
      hotpot?.setToken(null);
      await dispatch(realtime.leave(true));
    } catch (sessionEndError) {
      logger.error('[endSession] Session end error', sessionEndError);
    }
  };

  const endSessionBecauseProviderLeft = async (): Promise<void> => {
    logger.info('[endSession] Session has ended', {
      identityId,
      isAudioCall,
    });

    if (isAudioCall) {
      dispatch(setIsAudioCall(false));
    }

    try {
      void callHistory.recordCallEnd({
        dbSessionId: apolloSession.dbSessionId,
        roomId: !isProvider ? apolloSession.roomId : undefined,
      });
      await dispatchSessionEnd();
      /**
       * if call leaving sub-state is 'providerLeft' then don't leave realtime
       * We redirect user to waiting room and keep the patient checked-in.
       * Reset checkedInAt so the patient's wait timer restarts from 0 when
       * they return to the waiting room.
       */
      void dispatch(
        realtime.updateRealtimeState({
          status: RealtimeMemberStatus.Online,
          videoStartedAt: undefined,
          inCallWithProviderId: undefined,
          inCallWithProviderName: undefined,
          checkedInAt: getTime(),
        }),
      );
    } catch (sessionEndError) {
      logger.error('[endSession] Session end error', sessionEndError);
    }
  };
  /**
   * Ends the media session for a patient transfer without any post-call UI
   * (no survey, no notifications). Does not leave realtime because the patient
   * is transferring to a new room and stays connected.
   */
  const endPatientSessionForTransfer = async (): Promise<void> => {
    logger.info('[endSession] Session has ended', {
      identityId,
      isAudioCall,
    });

    if (isAudioCall) {
      dispatch(setIsAudioCall(false));
    }

    try {
      void callHistory.recordCallEnd({
        dbSessionId: apolloSession.dbSessionId,
        roomId: apolloSession.roomId,
      });
      await dispatchSessionEnd();
      // update the realtime state similar to the endSessionBecauseProviderLeft action
      void dispatch(
        realtime.updateRealtimeState({
          status: RealtimeMemberStatus.Online,
          videoStartedAt: undefined,
          inCallWithProviderId: undefined,
          inCallWithProviderName: undefined,
          checkedInAt: getTime(),
        }),
      );
    } catch (sessionEndError) {
      logger.error('[endSession] Session end error', sessionEndError);
    }
  };

  return {
    endSession,
    endSessionBecauseProviderLeft,
    endPatientSessionForTransfer,
  };
};
